import React, { useState, useEffect } from 'react';
import { compose } from 'redux';
import { connect } from 'react-redux';
import { useHistory, useLocation } from 'react-router-dom';

// Contexts
import { useConfiguration } from '../../context/configurationContext';
import { useRouteConfiguration } from '../../context/routeConfigurationContext';
// Utils
import { FormattedMessage, useIntl } from '../../util/reactIntl';
import { LISTING_STATE_PENDING_APPROVAL, LISTING_STATE_CLOSED, propTypes } from '../../util/types';
import { types as sdkTypes } from '../../util/sdkLoader';
import {
  LISTING_PAGE_DRAFT_VARIANT,
  LISTING_PAGE_PENDING_APPROVAL_VARIANT,
  LISTING_PAGE_PARAM_TYPE_DRAFT,
  LISTING_PAGE_PARAM_TYPE_EDIT,
  createSlug,
  NO_ACCESS_PAGE_USER_PENDING_APPROVAL,
  NO_ACCESS_PAGE_VIEW_LISTINGS,
} from '../../util/urlHelpers';
import {
  isErrorNoViewingPermission,
  isErrorUserPendingApproval,
  isForbiddenError,
} from '../../util/errors.js';
import { hasPermissionToViewData, isUserAuthorized } from '../../util/userHelpers.js';
import {
  ensureListing,
  ensureOwnListing,
  ensureUser,
  userDisplayNameAsString,
} from '../../util/data';
import { richText } from '../../util/richText';
import {
  isBookingProcess,
  isPurchaseProcess,
  resolveLatestProcessName,
} from '../../transactions/transaction';

// Global ducks (for Redux actions and thunks)
import { getMarketplaceEntities } from '../../ducks/marketplaceData.duck';
import { manageDisableScrolling, isScrollingDisabled } from '../../ducks/ui.duck';
import { initializeCardPaymentData } from '../../ducks/stripe.duck.js';

// Shared components
import {
  H4,
  Page,
  NamedLink,
  NamedRedirect,
  OrderPanel,
  LayoutSingleColumn,
} from '../../components';

// Related components and modules
import TopbarContainer from '../TopbarContainer/TopbarContainer';
import FooterContainer from '../FooterContainer/FooterContainer';
import NotFoundPage from '../NotFoundPage/NotFoundPage';

import {
  sendInquiry,
  setInitialValues,
  fetchTimeSlots,
  fetchTransactionLineItems,
} from './ListingPage.duck';

import {
  LoadingPage,
  ErrorPage,
  priceData,
  listingImages,
  handleContactUser,
  handleSubmitInquiry,
  handleSubmit,
  priceForSchemaMaybe,
  handleToggleFavorites,
} from './ListingPage.shared';
import { updateProfile } from '../ProfileSettingsPage/ProfileSettingsPage.duck';
import ActionBarMaybe from './ActionBarMaybe';
import SectionTextMaybe from './SectionTextMaybe';
import SectionReviews from './SectionReviews';
import SectionAuthorMaybe from './SectionAuthorMaybe';
import SectionMapMaybe from './SectionMapMaybe';
import SectionGallery from './SectionGallery';
import CustomListingFields from './CustomListingFields';

import css from './ListingPage.module.css';

const MIN_LENGTH_FOR_LONG_WORDS_IN_TITLE = 16;

const { UUID } = sdkTypes;

export const ListingPageComponent = props => {
  const [inquiryModalOpen, setInquiryModalOpen] = useState(
    props.inquiryModalOpenForListingId === props.params.id
  );
  const [mounted, setMounted] = useState(false);

  useEffect(() => {
    setMounted(true);
  }, []);

  const {
    isAuthenticated,
    currentUser,
    getListing,
    getOwnListing,
    intl,
    onManageDisableScrolling,
    params: rawParams,
    location,
    scrollingDisabled,
    showListingError,
    reviews = [],
    fetchReviewsError,
    sendInquiryInProgress,
    sendInquiryError,
    monthlyTimeSlots,
    onFetchTimeSlots,
    onFetchTransactionLineItems,
    lineItems,
    fetchLineItemsInProgress,
    fetchLineItemsError,
    history,
    callSetInitialValues,
    onSendInquiry,
    onInitializeCardPaymentData,
    config,
    routeConfiguration,
    showOwnListingsOnly,
    onUpdateFavorites,
  } = props;

  const listingConfig = config.listing;
  const listingId = new UUID(rawParams.id);
  const isVariant = rawParams.variant != null;
  const isPendingApprovalVariant = rawParams.variant === LISTING_PAGE_PENDING_APPROVAL_VARIANT;
  const isDraftVariant = rawParams.variant === LISTING_PAGE_DRAFT_VARIANT;
  const currentListing =
    isPendingApprovalVariant || isDraftVariant || showOwnListingsOnly
      ? ensureOwnListing(getOwnListing(listingId))
      : ensureListing(getListing(listingId));

  const listingSlug = rawParams.slug || createSlug(currentListing.attributes.title || '');
  const params = { slug: listingSlug, ...rawParams };

  const listingPathParamType = isDraftVariant
    ? LISTING_PAGE_PARAM_TYPE_DRAFT
    : LISTING_PAGE_PARAM_TYPE_EDIT;
  const listingTab = isDraftVariant ? 'photos' : 'details';

  const isApproved =
    currentListing.id && currentListing.attributes.state !== LISTING_STATE_PENDING_APPROVAL;

  const pendingIsApproved = isPendingApprovalVariant && isApproved;

  // If a /pending-approval URL is shared, the UI requires
  // authentication and attempts to fetch the listing from own
  // listings. This will fail with 403 Forbidden if the author is
  // another user. We use this information to try to fetch the
  // public listing.
  const pendingOtherUsersListing =
    (isPendingApprovalVariant || isDraftVariant) &&
    showListingError &&
    showListingError.status === 403;
  const shouldShowPublicListingPage = pendingIsApproved || pendingOtherUsersListing;

  if (shouldShowPublicListingPage) {
    return <NamedRedirect name="ListingPage" params={params} search={location.search} />;
  }

  const topbar = <TopbarContainer />;

  if (showListingError && showListingError.status === 404) {
    // 404 listing not found
    return <NotFoundPage staticContext={props.staticContext} />;
  } else if (showListingError) {
    // Other error in fetching listing
    return <ErrorPage topbar={topbar} scrollingDisabled={scrollingDisabled} intl={intl} />;
  } else if (!currentListing.id) {
    // Still loading the listing
    return <LoadingPage topbar={topbar} scrollingDisabled={scrollingDisabled} intl={intl} />;
  }

  const {
    description = '',
    geolocation = null,
    price = null,
    title = '',
    publicData = {},
    metadata = {},
  } = currentListing.attributes;

  const richTitle = (
    <span>
      {richText(title, {
        longWordMinLength: MIN_LENGTH_FOR_LONG_WORDS_IN_TITLE,
        longWordClass: css.longWord,
      })}
    </span>
  );

  const authorAvailable = currentListing && currentListing.author;
  const userAndListingAuthorAvailable = !!(currentUser && authorAvailable);
  const isOwnListing =
    userAndListingAuthorAvailable && currentListing.author.id.uuid === currentUser.id.uuid;

  const { listingType, transactionProcessAlias, unitType } = publicData;
  if (!(listingType && transactionProcessAlias && unitType)) {
    // Listing should always contain listingType, transactionProcessAlias and unitType)
    return (
      <ErrorPage topbar={topbar} scrollingDisabled={scrollingDisabled} intl={intl} invalidListing />
    );
  }
  const processName = resolveLatestProcessName(transactionProcessAlias.split('/')[0]);
  const isBooking = isBookingProcess(processName);
  const isPurchase = isPurchaseProcess(processName);
  const processType = isBooking ? 'booking' : isPurchase ? 'purchase' : 'inquiry';

  const currentAuthor = authorAvailable ? currentListing.author : null;
  const ensuredAuthor = ensureUser(currentAuthor);
  const noPayoutDetailsSetWithOwnListing =
    isOwnListing && (processType !== 'inquiry' && !currentUser?.attributes?.stripeConnected);
  const payoutDetailsWarning = noPayoutDetailsSetWithOwnListing ? (
    <span className={css.payoutDetailsWarning}>
      <FormattedMessage id="ListingPage.payoutDetailsWarning" values={{ processType }} />
      <NamedLink name="StripePayoutPage">
        <FormattedMessage id="ListingPage.payoutDetailsWarningLink" />
      </NamedLink>
    </span>
  ) : null;

  // When user is banned or deleted the listing is also deleted.
  // Because listing can be never showed with banned or deleted user we don't have to provide
  // banned or deleted display names for the function
  const authorDisplayName = userDisplayNameAsString(ensuredAuthor, '');

  const { formattedPrice } = priceData(price, config.currency, intl);

  const commonParams = { params, history, routes: routeConfiguration };
  const onContactUser = handleContactUser({
    ...commonParams,
    currentUser,
    callSetInitialValues,
    location,
    setInitialValues,
    setInquiryModalOpen,
  });
  // Note: this is for inquiry state in booking and purchase processes. Inquiry process is handled through handleSubmit.
  const onSubmitInquiry = handleSubmitInquiry({
    ...commonParams,
    getListing,
    onSendInquiry,
    setInquiryModalOpen,
  });
  const onSubmit = handleSubmit({
    ...commonParams,
    currentUser,
    callSetInitialValues,
    getListing,
    onInitializeCardPaymentData,
  });

  const handleOrderSubmit = values => {
    const isCurrentlyClosed = currentListing.attributes.state === LISTING_STATE_CLOSED;
    if (isOwnListing || isCurrentlyClosed) {
      window.scrollTo(0, 0);
    } else {
      onSubmit(values);
    }
  };

  const facebookImages = listingImages(currentListing, 'facebook');
  const twitterImages = listingImages(currentListing, 'twitter');
  const schemaImages = listingImages(
    currentListing,
    `${config.layout.listingImage.variantPrefix}-2x`
  ).map(img => img.url);
  const marketplaceName = config.marketplaceName;
  const schemaTitle = intl.formatMessage(
    { id: 'ListingPage.schemaTitle' },
    { title, price: formattedPrice, marketplaceName }
  );
  // You could add reviews, sku, etc. into page schema
  // Read more about product schema
  // https://developers.google.com/search/docs/advanced/structured-data/product
  const productURL = `${config.marketplaceRootURL}${location.pathname}${location.search}${location.hash}`;
  const currentStock = currentListing.currentStock?.attributes?.quantity || 0;
  const schemaAvailability = !currentListing.currentStock
    ? null
    : currentStock > 0
    ? 'https://schema.org/InStock'
    : 'https://schema.org/OutOfStock';

  const availabilityMaybe = schemaAvailability ? { availability: schemaAvailability } : {};

  const onToggleFavorites = handleToggleFavorites({
    ...commonParams,
    currentUser,
    onUpdateFavorites,
    location,
  });
  const userTypes = {
    company: 'Profesional',
    private: 'Privado',
  };
  const userType = userTypes[currentListing.author.attributes.profile.publicData.userType];
  const publicEngine = listingConfig.listingFields
    .find(el => el.key === 'engine')
    .enumOptions.find(el => el.option === publicData.engine)?.label;
  const publicTransmission = listingConfig.listingFields
    .find(el => el.key === 'transmission')
    .enumOptions.find(el => el.option === publicData.transmission)?.label;

  return (
    <Page
      title={schemaTitle}
      scrollingDisabled={scrollingDisabled}
      author={authorDisplayName}
      description={description}
      facebookImages={facebookImages}
      twitterImages={twitterImages}
      schema={{
        '@context': 'http://schema.org',
        '@type': 'Product',
        description: description,
        name: schemaTitle,
        image: schemaImages,
        offers: {
          '@type': 'Offer',
          url: productURL,
          ...priceForSchemaMaybe(price),
          ...availabilityMaybe,
        },
      }}
    >
      <LayoutSingleColumn className={css.pageRoot} topbar={topbar} footer={<FooterContainer />}>
        <div className={css.contentWrapperForProductLayout}>
          <div className={css.mainColumnForProductLayout}>
            {mounted && currentListing.id && noPayoutDetailsSetWithOwnListing ? (
              <ActionBarMaybe
                className={css.actionBarForProductLayout}
                isOwnListing={isOwnListing}
                listing={currentListing}
                showNoPayoutDetailsSet={noPayoutDetailsSetWithOwnListing}
                currentUser={currentUser}
              />
            ) : null}
            {mounted && currentListing.id ? (
              <ActionBarMaybe
                className={css.actionBarForProductLayout}
                isOwnListing={isOwnListing}
                listing={currentListing}
                currentUser={currentUser}
                editParams={{
                  id: listingId.uuid,
                  slug: listingSlug,
                  type: listingPathParamType,
                  tab: listingTab,
                }}
              />
            ) : null}
            <SectionGallery
              listing={currentListing}
              variantPrefix={config.layout.listingImage.variantPrefix}
            />
            <div className={css.mobileHeading}>
              <H4 as="h1" className={css.orderPanelTitle}>
                <FormattedMessage id="ListingPage.orderTitle" values={{ title: richTitle }} />
              </H4>
            </div>
            <SectionTextMaybe heading="Descripción" text={description} showAsIngress />

            <section className={css.shortInfo}>
              <div className={css.shortInfo_gap}>
                <svg
                  width="24"
                  height="25"
                  viewBox="0 0 24 25"
                  fill="none"
                  xmlns="http://www.w3.org/2000/svg"
                >
                  <path
                    d="M5.5 3.37988L3 21.3799M18.5 3.37988L20.982 21.3574M12 3.37988V6.37988M12 17.8799V21.3799M12 10.3799V13.8799"
                    stroke="black"
                    strokeWidth="2"
                    strokeLinecap="round"
                    strokeLinejoin="round"
                  />
                </svg>
                <div className={css.shortInfo_values}>
                  Kilometraje
                  <span>{publicData.mileage} km</span>
                </div>
              </div>
              {publicEngine && (
                <div className={css.shortInfo_gap}>
                  <svg
                    width="24"
                    height="25"
                    viewBox="0 0 24 25"
                    fill="none"
                    xmlns="http://www.w3.org/2000/svg"
                  >
                    <path
                      d="M3 2.37988H6C6.28 2.37988 6.53 2.48988 6.71 2.66988L8.79 4.75988L9.59 3.96988C10 3.57988 10.5 3.37988 11 3.37988H17C17.5 3.37988 18 3.57988 18.41 3.96988L19.41 4.96988C19.8 5.37988 20 5.87988 20 6.37988V19.3799C20 19.9103 19.7893 20.419 19.4142 20.7941C19.0391 21.1692 18.5304 21.3799 18 21.3799H8C7.46957 21.3799 6.96086 21.1692 6.58579 20.7941C6.21071 20.419 6 19.9103 6 19.3799V8.37988C6 7.87988 6.2 7.37988 6.59 6.96988L7.38 6.16988L5.59 4.37988H3V2.37988ZM11 5.37988V7.37988H17V5.37988H11ZM11.41 11.3799L9.41 9.37988H8V10.7899L10 12.7899V15.9699L8 17.9699V19.3799H9.41L11.41 17.3799H14.59L16.59 19.3799H18V17.9699L16 15.9699V12.7899L18 10.7899V9.37988H16.59L14.59 11.3799H11.41ZM12 13.3799H14V15.3799H12V13.3799Z"
                      fill="black"
                    />
                  </svg>

                  <div className={css.shortInfo_values}>
                    Motor
                    <span>{publicEngine}</span>
                  </div>
                </div>
              )}

              <div className={css.shortInfo_gap}>
                <svg
                  width="24"
                  height="25"
                  viewBox="0 0 24 25"
                  fill="none"
                  xmlns="http://www.w3.org/2000/svg"
                >
                  <path
                    d="M5 4.37988V20.3799M12 4.37988V20.3799M19 4.37988V12.3799H5"
                    stroke="black"
                    strokeWidth="2"
                    strokeLinecap="round"
                    strokeLinejoin="round"
                  />
                </svg>

                <div className={css.shortInfo_values}>
                  Transmisión
                  <span>{publicTransmission}</span>
                </div>
              </div>
              <div className={css.shortInfo_gap}>
                <svg
                  width="24"
                  height="25"
                  viewBox="0 0 24 25"
                  fill="none"
                  xmlns="http://www.w3.org/2000/svg"
                >
                  <path
                    d="M11 3.93788V5.37988C11 5.6451 11.1054 5.89945 11.2929 6.08699C11.4804 6.27453 11.7348 6.37988 12 6.37988C12.2652 6.37988 12.5196 6.27453 12.7071 6.08699C12.8946 5.89945 13 5.6451 13 5.37988V3.93788C14.8958 4.16298 16.6606 5.01941 18.0106 6.36933C19.3605 7.71925 20.2169 9.48413 20.442 11.3799H19C18.7348 11.3799 18.4804 11.4852 18.2929 11.6728C18.1054 11.8603 18 12.1147 18 12.3799C18 12.6451 18.1054 12.8995 18.2929 13.087C18.4804 13.2745 18.7348 13.3799 19 13.3799H20.44C20.216 15.1969 19.396 16.8279 18.2 18.1199C18.0647 18.2658 17.993 18.4594 18.0005 18.6582C18.008 18.857 18.0941 19.0446 18.24 19.1799C18.3859 19.3151 18.5795 19.3869 18.7783 19.3794C18.9771 19.3719 19.1647 19.2858 19.3 19.1399C20.943 17.3649 22 15.0129 22 12.3799C22 6.85688 17.523 2.37988 12 2.37988C6.477 2.37988 2 6.85688 2 12.3799C2 15.0129 3.057 17.3649 4.7 19.1399C4.76697 19.2121 4.84752 19.2704 4.93703 19.3115C5.02655 19.3526 5.12329 19.3757 5.22172 19.3794C5.32015 19.3831 5.41834 19.3674 5.5107 19.3332C5.60306 19.2989 5.68777 19.2469 5.76 19.1799C5.83223 19.1129 5.89056 19.0324 5.93165 18.9428C5.97275 18.8533 5.99582 18.7566 5.99953 18.6582C6.00325 18.5597 5.98754 18.4615 5.9533 18.3692C5.91907 18.2768 5.86697 18.1921 5.8 18.1199C4.604 16.8289 3.784 15.1979 3.56 13.3799H5C5.26522 13.3799 5.51957 13.2745 5.70711 13.087C5.89464 12.8995 6 12.6451 6 12.3799C6 12.1147 5.89464 11.8603 5.70711 11.6728C5.51957 11.4852 5.26522 11.3799 5 11.3799H3.558C3.74046 9.82305 4.35186 8.34751 5.324 7.11788L6.293 8.08788C6.38591 8.18073 6.4962 8.25436 6.61757 8.30458C6.73893 8.35481 6.869 8.38063 7.00035 8.38059C7.1317 8.38054 7.26176 8.35462 7.38309 8.30431C7.50442 8.25401 7.61466 8.18029 7.7075 8.08738C7.80034 7.99447 7.87398 7.88418 7.9242 7.76282C7.97442 7.64145 8.00025 7.51138 8.0002 7.38003C8.00016 7.24868 7.97424 7.11863 7.92393 6.9973C7.87362 6.87596 7.79991 6.76573 7.707 6.67288L6.738 5.70388C7.96763 4.73175 9.44316 4.12034 11 3.93788ZM16.759 7.01388C16.6428 6.92438 16.4995 6.87711 16.3529 6.87984C16.2062 6.88256 16.0648 6.93513 15.952 7.02888L15.734 7.21188C14.9478 7.87217 14.1641 8.5355 13.383 9.20188C12.727 9.76188 12.056 10.3419 11.52 10.8149C11.2879 11.0189 11.0583 11.2256 10.831 11.4349C10.6827 11.5677 10.5424 11.7093 10.411 11.8589C10.2559 12.0498 10.1407 12.27 10.0723 12.5062C10.0039 12.7425 9.98366 12.9901 10.0128 13.2344C10.0419 13.4787 10.1197 13.7146 10.2417 13.9282C10.3637 14.1418 10.5274 14.3287 10.723 14.4779C11.1214 14.785 11.6238 14.9249 12.1236 14.8678C12.6233 14.8106 13.0812 14.561 13.4 14.1719C13.488 14.0639 13.605 13.8759 13.725 13.6749C13.853 13.4599 14.01 13.1849 14.184 12.8769C14.531 12.2569 14.952 11.4849 15.359 10.7319C15.8424 9.83486 16.322 8.93585 16.798 8.03488L16.929 7.78588C16.9969 7.65797 17.0171 7.51006 16.986 7.36862C16.9548 7.22718 16.8744 7.10143 16.759 7.01388Z"
                    fill="black"
                  />
                </svg>

                <div className={css.shortInfo_values}>
                  Potencia
                  <span>
                    {publicData.horsepower || publicData.Power}{' '}
                    {publicData.engine === 'electric' || publicData.engine === 'hybrid'
                      ? 'kwH'
                      : 'CV'}
                  </span>
                </div>
              </div>

              <div className={css.shortInfo_gap}>
                <svg
                  width="24"
                  height="25"
                  viewBox="0 0 24 25"
                  fill="none"
                  xmlns="http://www.w3.org/2000/svg"
                >
                  <path
                    d="M19 4.37988H17V3.37988C17 3.11467 16.8946 2.86031 16.7071 2.67278C16.5196 2.48524 16.2652 2.37988 16 2.37988C15.7348 2.37988 15.4804 2.48524 15.2929 2.67278C15.1054 2.86031 15 3.11467 15 3.37988V4.37988H9V3.37988C9 3.11467 8.89464 2.86031 8.70711 2.67278C8.51957 2.48524 8.26522 2.37988 8 2.37988C7.73478 2.37988 7.48043 2.48524 7.29289 2.67278C7.10536 2.86031 7 3.11467 7 3.37988V4.37988H5C4.20435 4.37988 3.44129 4.69595 2.87868 5.25856C2.31607 5.82117 2 6.58423 2 7.37988V19.3799C2 20.1755 2.31607 20.9386 2.87868 21.5012C3.44129 22.0638 4.20435 22.3799 5 22.3799H19C19.7956 22.3799 20.5587 22.0638 21.1213 21.5012C21.6839 20.9386 22 20.1755 22 19.3799V7.37988C22 6.58423 21.6839 5.82117 21.1213 5.25856C20.5587 4.69595 19.7956 4.37988 19 4.37988ZM20 19.3799C20 19.6451 19.8946 19.8995 19.7071 20.087C19.5196 20.2745 19.2652 20.3799 19 20.3799H5C4.73478 20.3799 4.48043 20.2745 4.29289 20.087C4.10536 19.8995 4 19.6451 4 19.3799V12.3799H20V19.3799ZM20 10.3799H4V7.37988C4 7.11467 4.10536 6.86031 4.29289 6.67278C4.48043 6.48524 4.73478 6.37988 5 6.37988H7V7.37988C7 7.6451 7.10536 7.89945 7.29289 8.08699C7.48043 8.27453 7.73478 8.37988 8 8.37988C8.26522 8.37988 8.51957 8.27453 8.70711 8.08699C8.89464 7.89945 9 7.6451 9 7.37988V6.37988H15V7.37988C15 7.6451 15.1054 7.89945 15.2929 8.08699C15.4804 8.27453 15.7348 8.37988 16 8.37988C16.2652 8.37988 16.5196 8.27453 16.7071 8.08699C16.8946 7.89945 17 7.6451 17 7.37988V6.37988H19C19.2652 6.37988 19.5196 6.48524 19.7071 6.67278C19.8946 6.86031 20 7.11467 20 7.37988V10.3799Z"
                    fill="black"
                  />
                </svg>

                <div className={css.shortInfo_values}>
                  Ano
                  <span>{publicData.year}</span>
                </div>
              </div>
              <div className={css.shortInfo_gap}>
                <svg
                  width="20"
                  height="21"
                  viewBox="0 0 20 21"
                  fill="none"
                  xmlns="http://www.w3.org/2000/svg"
                >
                  <g clipPath="url(#clip0_432_4066)">
                    <path
                      d="M10 8.37988C11.7 8.37988 13.06 7.02988 13.06 5.37988C13.06 3.72988 11.7 2.37988 10 2.37988C8.3 2.37988 6.94 3.72988 6.94 5.37988C6.94 7.02988 8.3 8.37988 10 8.37988ZM10 10.3799C7.2 10.3799 4.94 8.13988 4.94 5.37988C4.94 2.61988 7.2 0.379883 10 0.379883C12.8 0.379883 15.06 2.61988 15.06 5.37988C15.06 8.13988 12.8 10.3799 10 10.3799ZM3 18.3799H17V17.0499C17 15.2999 14.69 13.4899 10 13.4899C5.31 13.4899 3 15.2999 3 17.0499V18.3799ZM10 11.4899C16.66 11.4899 19 14.8199 19 17.0499V20.3799H1V17.0499C1 14.8199 3.34 11.4899 10 11.4899Z"
                      fill="black"
                    />
                  </g>
                  <defs>
                    <clipPath id="clip0_432_4066">
                      <rect width="20" height="20" fill="white" transform="translate(0 0.379883)" />
                    </clipPath>
                  </defs>
                </svg>

                <div className={css.shortInfo_values}>
                  Vendedor
                  <span>{userType}</span>
                </div>
              </div>
            </section>

            <CustomListingFields
              publicData={publicData}
              metadata={metadata}
              listingFieldConfigs={listingConfig.listingFields}
              categoryConfiguration={config.categoryConfiguration}
              intl={intl}
            />

            <SectionMapMaybe
              geolocation={geolocation}
              publicData={publicData}
              listingId={currentListing.id}
              mapsConfig={config.maps}
            />
            {/* <SectionReviews reviews={reviews} fetchReviewsError={fetchReviewsError} /> */}
            {/* <SectionAuthorMaybe
              title={title}
              listing={currentListing}
              authorDisplayName={authorDisplayName}
              onContactUser={onContactUser}
              isInquiryModalOpen={isAuthenticated && inquiryModalOpen}
              onCloseInquiryModal={() => setInquiryModalOpen(false)}
              sendInquiryError={sendInquiryError}
              sendInquiryInProgress={sendInquiryInProgress}
              onSubmitInquiry={onSubmitInquiry}
              currentUser={currentUser}
              onManageDisableScrolling={onManageDisableScrolling}
            /> */}
          </div>
          <div className={css.orderColumnForProductLayout}>
            <OrderPanel
              currentUser={currentUser}
              onToggleFavorites={onToggleFavorites}
              className={css.productOrderPanel}
              listing={currentListing}
              isOwnListing={isOwnListing}
              onSubmit={handleOrderSubmit}
              userType={userType}
              authorLink={
                <NamedLink
                  className={css.authorNameLink}
                  name={isVariant ? 'ListingPageVariant' : 'ListingPage'}
                  params={params}
                  to={{ hash: '#author' }}
                >
                  {authorDisplayName}
                </NamedLink>
              }
              title={<FormattedMessage id="ListingPage.orderTitle" values={{ title: richTitle }} />}
              titleDesktop={
                <H4 as="h1" className={css.orderPanelTitle}>
                  <FormattedMessage id="ListingPage.orderTitle" values={{ title: richTitle }} />
                </H4>
              }
              payoutDetailsWarning={payoutDetailsWarning}
              author={ensuredAuthor}
              onManageDisableScrolling={onManageDisableScrolling}
              onContactUser={onContactUser}
              monthlyTimeSlots={monthlyTimeSlots}
              onFetchTimeSlots={onFetchTimeSlots}
              onFetchTransactionLineItems={onFetchTransactionLineItems}
              lineItems={lineItems}
              fetchLineItemsInProgress={fetchLineItemsInProgress}
              fetchLineItemsError={fetchLineItemsError}
              validListingTypes={config.listing.listingTypes}
              marketplaceCurrency={config.currency}
              dayCountAvailableForBooking={config.stripe.dayCountAvailableForBooking}
              marketplaceName={config.marketplaceName}
            />
          </div>
        </div>
      </LayoutSingleColumn>
    </Page>
  );
};

/**
 * The ListingPage component with carousel layout.
 *
 * @component
 * @param {Object} props
 * @param {Object} props.params - The path params object
 * @param {string} props.params.id - The listing id
 * @param {string} props.params.slug - The listing slug
 * @param {LISTING_PAGE_DRAFT_VARIANT | LISTING_PAGE_PENDING_APPROVAL_VARIANT} props.params.variant - The listing variant
 * @param {Function} props.onManageDisableScrolling - The on manage disable scrolling function
 * @param {boolean} props.isAuthenticated - Whether the user is authenticated
 * @param {Function} props.getListing - The get listing function
 * @param {Function} props.getOwnListing - The get own listing function
 * @param {Object} props.currentUser - The current user
 * @param {boolean} props.scrollingDisabled - Whether scrolling is disabled
 * @param {string} props.inquiryModalOpenForListingId - The inquiry modal open for the specific listing id
 * @param {propTypes.error} props.showListingError - The show listing error
 * @param {Function} props.callSetInitialValues - The call setInitialValues function, which is given to this function as a parameter
 * @param {Array<propTypes.review>} props.reviews - The reviews
 * @param {propTypes.error} props.fetchReviewsError - The fetch reviews error
 * @param {Object<string, Object>} props.monthlyTimeSlots - The monthly time slots. E.g. { '2019-11': { timeSlots: [], fetchTimeSlotsInProgress: false, fetchTimeSlotsError: null } }
 * @param {boolean} props.sendInquiryInProgress - Whether the send inquiry is in progress
 * @param {propTypes.error} props.sendInquiryError - The send inquiry error
 * @param {Function} props.onSendInquiry - The on send inquiry function
 * @param {Function} props.onInitializeCardPaymentData - The on initialize card payment data function
 * @param {Function} props.onFetchTimeSlots - The on fetch time slots function
 * @param {Function} props.onFetchTransactionLineItems - The on fetch transaction line items function
 * @param {Array<propTypes.transactionLineItem>} props.lineItems - The line items
 * @param {boolean} props.fetchLineItemsInProgress - Whether the fetch line items is in progress
 * @param {propTypes.error} props.fetchLineItemsError - The fetch line items error
 * @returns {JSX.Element} listing page component
 */
const EnhancedListingPage = props => {
  const config = useConfiguration();
  const routeConfiguration = useRouteConfiguration();
  const intl = useIntl();
  const history = useHistory();
  const location = useLocation();

  const showListingError = props.showListingError;
  const isVariant = props.params?.variant != null;
  const currentUser = props.currentUser;
  if (isForbiddenError(showListingError) && !isVariant && !currentUser) {
    // This can happen if private marketplace mode is active
    return (
      <NamedRedirect
        name="SignupPage"
        state={{ from: `${location.pathname}${location.search}${location.hash}` }}
      />
    );
  }

  const isPrivateMarketplace = config.accessControl.marketplace.private === true;
  const isUnauthorizedUser = currentUser && !isUserAuthorized(currentUser);
  const hasNoViewingRights = currentUser && !hasPermissionToViewData(currentUser);
  const hasUserPendingApprovalError = isErrorUserPendingApproval(showListingError);

  if ((isPrivateMarketplace && isUnauthorizedUser) || hasUserPendingApprovalError) {
    return (
      <NamedRedirect
        name="NoAccessPage"
        params={{ missingAccessRight: NO_ACCESS_PAGE_USER_PENDING_APPROVAL }}
      />
    );
  } else if (
    (hasNoViewingRights && isForbiddenError(showListingError)) ||
    isErrorNoViewingPermission(showListingError)
  ) {
    // If the user has no viewing rights, fetching anything but their own listings
    // will return a 403 error. If that happens, redirect to NoAccessPage.
    return (
      <NamedRedirect
        name="NoAccessPage"
        params={{ missingAccessRight: NO_ACCESS_PAGE_VIEW_LISTINGS }}
      />
    );
  }

  return (
    <ListingPageComponent
      config={config}
      routeConfiguration={routeConfiguration}
      intl={intl}
      history={history}
      location={location}
      showOwnListingsOnly={hasNoViewingRights}
      {...props}
    />
  );
};

const mapStateToProps = state => {
  const { isAuthenticated } = state.auth;
  const {
    showListingError,
    reviews,
    fetchReviewsError,
    monthlyTimeSlots,
    sendInquiryInProgress,
    sendInquiryError,
    lineItems,
    fetchLineItemsInProgress,
    fetchLineItemsError,
    inquiryModalOpenForListingId,
  } = state.ListingPage;
  const { currentUser } = state.user;

  const getListing = id => {
    const ref = { id, type: 'listing' };
    const listings = getMarketplaceEntities(state, [ref]);
    return listings.length === 1 ? listings[0] : null;
  };

  const getOwnListing = id => {
    const ref = { id, type: 'ownListing' };
    const listings = getMarketplaceEntities(state, [ref]);
    return listings.length === 1 ? listings[0] : null;
  };

  return {
    isAuthenticated,
    currentUser,
    getListing,
    getOwnListing,
    scrollingDisabled: isScrollingDisabled(state),
    inquiryModalOpenForListingId,
    showListingError,
    reviews,
    fetchReviewsError,
    monthlyTimeSlots,
    lineItems,
    fetchLineItemsInProgress,
    fetchLineItemsError,
    sendInquiryInProgress,
    sendInquiryError,
  };
};

const mapDispatchToProps = dispatch => ({
  onManageDisableScrolling: (componentId, disableScrolling) =>
    dispatch(manageDisableScrolling(componentId, disableScrolling)),
  callSetInitialValues: (setInitialValues, values, saveToSessionStorage) =>
    dispatch(setInitialValues(values, saveToSessionStorage)),
  onFetchTransactionLineItems: params => dispatch(fetchTransactionLineItems(params)),
  onSendInquiry: (listing, message) => dispatch(sendInquiry(listing, message)),
  onInitializeCardPaymentData: () => dispatch(initializeCardPaymentData()),
  onFetchTimeSlots: (listingId, start, end, timeZone) =>
    dispatch(fetchTimeSlots(listingId, start, end, timeZone)),
  onUpdateFavorites: payload => dispatch(updateProfile(payload)),
});

// Note: it is important that the withRouter HOC is **outside** the
// connect HOC, otherwise React Router won't rerender any Route
// components since connect implements a shouldComponentUpdate
// lifecycle hook.
//
// See: https://github.com/ReactTraining/react-router/issues/4671
const ListingPage = compose(
  connect(
    mapStateToProps,
    mapDispatchToProps
  )
)(EnhancedListingPage);

export default ListingPage;
